import { useRef, useState } from 'react';
import { Accordion, Badge, Button, Form, Spinner, useAccordionButton } from 'react-bootstrap';
import { ChevronDown, Languages, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { LOCALIZATION_LANGUAGES, getLanguageByCode } from '../../shared/constants/languages.js';
import { FormField, FormSection } from '../../shared/ui/FormLayout';
import { EmptyState } from '../../shared/ui/EmptyState';
import { ConfirmModal } from '../../shared/ui/ConfirmModal/ConfirmModal.jsx';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { UploadPicture } from '../UploadPicture/index.js';
import { ContentEditor } from '../ContentEditor/ContentEditor.jsx';
import { GalleryEditor } from '../GalleryEditor/index.js';
import { applyTranslation, buildTranslateRequest } from './aiTranslate.js';
import './LocalizationsBlock.css';

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Поле локализации:
 *  { name, label, type: 'text' | 'textarea' | 'content' | 'image' | 'file' | 'list' | 'seo' | 'gallery', required?, hint?, rows?,
 *    entityType? (image — пропорции кропа), itemKey? (list — поле элемента, по умолчанию 'title'), itemLabel?,
 *    formField? (image/file — суффикс поля формы localization_{LANG}_{formField}; нужен, когда файлов у локализации несколько),
 *    accept? (file — допустимые расширения), clearable? (image/file — false, если бэкенд не умеет сбрасывать файл) }
 *  gallery — список фото [{ key, imgPath } | { key, file }] (см. GalleryEditor); в форму его раскладывает
 *    buildEntityFormData({ galleryFields })
 */

const emptyValueFor = (field) => {
  switch (field.type) {
    case 'content':
      return { items: [] };
    case 'list':
    case 'gallery':
      return [];
    case 'seo':
      return { title: '', description: '', keywords: '' };
    default:
      return '';
  }
};

const createLocalization = (code, fields) =>
  fields.reduce((acc, field) => ({ ...acc, [field.name]: emptyValueFor(field) }), { language: code });

const isBlank = (value) => !String(value ?? '').trim();

/**
 * Проверка перед сохранением: возвращает текст ошибки или null
 */
export const validateLocalizations = (localizations = [], fields = []) => {
  for (const localization of localizations) {
    const missing = fields.filter((f) => f.required && isBlank(localization[f.name]));
    if (missing.length) {
      const lang = getLanguageByCode(localization.language);
      return `Локализация «${lang?.title || localization.language}»: заполните ${missing.map((f) => `«${f.label}»`).join(', ')}`;
    }
  }
  return null;
};

const sortByLanguage = (items) =>
  [...items].sort(
    (a, b) =>
      LOCALIZATION_LANGUAGES.findIndex((l) => l.code === a.language) -
      LOCALIZATION_LANGUAGES.findIndex((l) => l.code === b.language),
  );

const HeaderToggle = ({ eventKey, children }) => {
  const toggle = useAccordionButton(eventKey);
  return (
    <button type="button" className="loc-card__toggle" onClick={toggle}>
      {children}
      <ChevronDown size={18} className="loc-card__chevron" />
    </button>
  );
};

const ListField = ({ field, value = [], onChange }) => {
  const itemKey = field.itemKey || 'title';
  return (
    <div className="loc-list">
      {value.map((item, index) => (
        <div key={index} className="loc-list__row">
          <Form.Control
            size="sm"
            value={item[itemKey] || ''}
            placeholder={field.itemLabel || 'Значение'}
            onChange={(e) => onChange(value.map((v, i) => (i === index ? { ...v, [itemKey]: e.target.value } : v)))}
          />
          <Button variant="link" size="sm" className="loc-list__remove" onClick={() => onChange(value.filter((_, i) => i !== index))}>
            <X size={16} />
          </Button>
        </div>
      ))}
      <Button variant="outline-secondary" size="sm" onClick={() => onChange([...value, { [itemKey]: '' }])}>
        <Plus size={14} className="me-1" />
        Добавить
      </Button>
    </div>
  );
};

const LocalizationField = ({ field, localization, onPatch }) => {
  const value = localization[field.name];

  switch (field.type) {
    case 'textarea':
      return (
        <FormField label={field.label} required={field.required} hint={field.hint}>
          <Form.Control as="textarea" rows={field.rows || 3} value={value || ''} onChange={(e) => onPatch({ [field.name]: e.target.value })} />
        </FormField>
      );

    case 'content':
      return (
        <div className="mb-4">
          <ContentEditor
            key={`${localization.language}-${field.name}-${localization._aiVersion || 0}`}
            label={field.label}
            value={value?.items || []}
            onChange={(items) => onPatch({ [field.name]: { ...(value || {}), items } })}
          />
          {field.hint && <Form.Text className="text-muted">{field.hint}</Form.Text>}
        </div>
      );

    case 'image':
    case 'file': {
      const isDocument = field.type === 'file';
      // С formField файл кладётся в _files[formField] → localization_{LANG}_{formField}; без него — прежний _imageFile
      const selectedFile = field.formField ? localization._files?.[field.formField] : localization._imageFile;
      const selectFile = (file) =>
        field.formField
          ? onPatch({ _files: { ...(localization._files || {}), [field.formField]: file } })
          : onPatch({ _imageFile: file });
      const clearFile = () => {
        const fileChanges = field.formField
          ? { _files: { ...(localization._files || {}), [field.formField]: null } }
          : { _imageFile: null };
        onPatch({ [field.name]: '', ...fileChanges, _imageKey: (localization._imageKey || 0) + 1 });
      };
      const hasOwnFile = Boolean(value) || Boolean(selectedFile);
      const defaultHint = isDocument
        ? 'Необязательно. Без своего файла на сайте показывается основной.'
        : 'Необязательно. Без своей картинки на сайте показывается основная.';

      return (
        <FormField label={field.label} hint={field.hint || defaultHint}>
          <UploadPicture
            key={localization._imageKey || 0}
            id={`localization-${localization.language}-${field.name}`}
            initialValue={value || undefined}
            entityType={field.entityType}
            accept={isDocument ? field.accept || '*' : undefined}
            onSelect={selectFile}
            onRemove={hasOwnFile && field.clearable !== false ? clearFile : undefined}
          />
        </FormField>
      );
    }

    case 'list':
      return (
        <FormField label={field.label} hint={field.hint}>
          <ListField field={field} value={value || []} onChange={(next) => onPatch({ [field.name]: next })} />
        </FormField>
      );

    case 'gallery':
      return (
        <FormField label={field.label} hint={field.hint}>
          <GalleryEditor value={value || []} onChange={(next) => onPatch({ [field.name]: next })} />
        </FormField>
      );

    case 'seo':
      return (
        <div className="loc-card__seo">
          <h6 className="loc-card__subtitle">{field.label || 'SEO'}</h6>
          <SEOMetatagsForm seoMetatags={value || {}} onChange={(next) => onPatch({ [field.name]: next })} />
        </div>
      );

    default:
      return (
        <FormField label={field.label} required={field.required} hint={field.hint}>
          <Form.Control type="text" value={value || ''} onChange={(e) => onPatch({ [field.name]: e.target.value })} />
        </FormField>
      );
  }
};

/**
 * Блок «Локализации» внизу формы сущности: карточка на каждый язык в аккордеоне.
 * Значение сохраняется вместе с сущностью (Create/Update) — отдельных запросов нет.
 *
 * @param {Array} value — [{ language: 'EN', ...поля }]
 * @param {Function} onChange
 * @param {Array} fields — конфиг полей (см. выше)
 * @param {string} [titleField='title'] — поле для превью в заголовке карточки
 * @param {object} [source] — текущие значения основной (русской) формы с теми же именами полей, что в fields.
 *   Если передан — появляются кнопки «Перевести с ИИ» (по языку и все языки сразу)
 * @param {string} [aiContext] — подсказка ИИ, что переводим (например «статья блога»)
 */
export const LocalizationsBlock = ({
  value = [],
  onChange,
  fields,
  titleField = 'title',
  title = 'Локализации',
  source,
  aiContext,
}) => {
  const [openKeys, setOpenKeys] = useState([]);
  const [languageToRemove, setLanguageToRemove] = useState(null);
  // null | 'ALL' | код языка — какой перевод сейчас идёт
  const [translating, setTranslating] = useState(null);
  const [confirmTranslateAll, setConfirmTranslateAll] = useState(false);

  // Ответ ИИ приходит через несколько секунд — применяем его к актуальному значению, а не к замкнутому
  const valueRef = useRef(value);
  valueRef.current = value;

  const used = new Set(value.map((i) => i.language));
  const missingLanguages = LOCALIZATION_LANGUAGES.filter((l) => !used.has(l.code));

  const add = (code) => {
    onChange(sortByLanguage([...value, createLocalization(code, fields)]));
    setOpenKeys((keys) => [...keys, code]);
  };

  const patch = (code, changes) => {
    onChange(value.map((i) => (i.language === code ? { ...i, ...changes } : i)));
  };

  const remove = (code) => {
    onChange(value.filter((i) => i.language !== code));
    setOpenKeys((keys) => keys.filter((k) => k !== code));
  };

  const translate = async (codes) => {
    const request = buildTranslateRequest(fields, source, codes, aiContext);
    if (!request) {
      toast.error('Нечего переводить: сначала заполните основные поля на русском');
      return;
    }

    setTranslating(codes.length === 1 ? codes[0] : 'ALL');
    try {
      const res = await apiRequests.ai.translateEntity(request);
      const translations = res?.translations || {};
      let next = [...valueRef.current];
      codes.forEach((code) => {
        // Сериализатор бэкенда может привести ключи словаря к camelCase (EN → en)
        const translation = translations[code] || translations[code.toLowerCase()];
        if (!translation) return;
        const existing = next.find((i) => i.language === code) || createLocalization(code, fields);
        const updated = applyTranslation(existing, fields, source, translation);
        next = next.some((i) => i.language === code)
          ? next.map((i) => (i.language === code ? updated : i))
          : [...next, updated];
      });
      onChange(sortByLanguage(next));
      setOpenKeys((keys) => [...new Set([...keys, ...codes])]);
      toast.success(
        codes.length === 1
          ? `Перевод (${codes[0]}) готов — проверьте его перед сохранением`
          : 'Переводы готовы — проверьте их перед сохранением',
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось перевести с ИИ'));
    } finally {
      setTranslating(null);
    }
  };

  const allCodes = LOCALIZATION_LANGUAGES.map((l) => l.code);
  const handleTranslateAll = () => {
    if (value.length) setConfirmTranslateAll(true);
    else translate(allCodes);
  };

  const hasMediaContent = fields.some(
    (f) => f.type === 'content' && (source?.[f.name]?.items || []).some((i) => i.discriminator !== 'TextContentItem'),
  );

  const requiredFields = fields.filter((f) => f.required);
  const removeLanguage = getLanguageByCode(languageToRemove);

  return (
    <FormSection title={title}>
      <div className="loc-block">
        <div className="loc-block__head">
          <p className="loc-block__hint">
            Основной язык — русский, он заполняется в полях выше. Переводы сохраняются вместе с сущностью.
            {source && hasMediaContent && ' ИИ переводит только текст: картинки и медиа контента добавьте в перевод вручную.'}
          </p>
          {source && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleTranslateAll}
              disabled={Boolean(translating)}
              title="Заполнить переводы на все языки из полей выше"
            >
              {translating === 'ALL' ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Sparkles size={14} className="me-1" />
              )}
              {translating === 'ALL' ? 'Переводим…' : 'Перевести все языки с ИИ'}
            </Button>
          )}
          {missingLanguages.length > 0 && (
            <div className="loc-block__add">
              {missingLanguages.map((lang) => (
                <Button key={lang.code} variant="outline-secondary" size="sm" onClick={() => add(lang.code)} title={`Добавить: ${lang.title}`}>
                  <Plus size={14} className="me-1" />
                  {lang.code}
                </Button>
              ))}
            </div>
          )}
        </div>

        {value.length === 0 ? (
          <EmptyState
            compact
            icon={Languages}
            title="Переводов пока нет"
            description="Без перевода страница на других языках сайта откроется на русском. Добавьте язык кнопками выше."
          />
        ) : (
          <Accordion alwaysOpen activeKey={openKeys} onSelect={(keys) => setOpenKeys(keys || [])} className="loc-block__list">
            {value.map((localization) => {
              const lang = getLanguageByCode(localization.language);
              const missing = requiredFields.filter((f) => isBlank(localization[f.name]));
              const preview = localization[titleField];

              return (
                <Accordion.Item key={localization.language} eventKey={localization.language} className="loc-card">
                  <div className="loc-card__header">
                    <HeaderToggle eventKey={localization.language}>
                      <span className="loc-card__code">{localization.language}</span>
                      <span className="loc-card__lang">
                        {lang?.title || localization.language}
                        <span className="loc-card__native">{lang?.native}</span>
                      </span>
                      <span className="loc-card__preview">{preview || 'Без названия'}</span>
                      {missing.length ? (
                        <Badge bg="warning" text="dark" className="loc-card__status">
                          Не заполнено
                        </Badge>
                      ) : (
                        <Badge bg="success" className="loc-card__status">
                          Готово
                        </Badge>
                      )}
                    </HeaderToggle>
                    {source && (
                      <Button
                        variant="link"
                        className="loc-card__ai"
                        title="Перевести с ИИ из полей выше"
                        disabled={Boolean(translating)}
                        onClick={() => translate([localization.language])}
                      >
                        {translating === localization.language || translating === 'ALL' ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Sparkles size={17} />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="link"
                      className="loc-card__remove"
                      title="Удалить локализацию"
                      onClick={() => setLanguageToRemove(localization.language)}
                    >
                      <Trash2 size={17} />
                    </Button>
                  </div>
                  <Accordion.Body className="loc-card__body">
                    {fields.map((field) => (
                      <LocalizationField
                        key={field.name}
                        field={field}
                        localization={localization}
                        onPatch={(changes) => patch(localization.language, changes)}
                      />
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </div>

      <ConfirmModal
        show={Boolean(languageToRemove)}
        onHide={() => setLanguageToRemove(null)}
        onConfirm={() => remove(languageToRemove)}
        title="Удалить локализацию?"
        message={`Перевод на ${removeLanguage?.title?.toLowerCase() || languageToRemove} будет удалён после сохранения формы.`}
        confirmButtonText="Удалить"
      />

      <ConfirmModal
        show={confirmTranslateAll}
        onHide={() => setConfirmTranslateAll(false)}
        onConfirm={() => translate(allCodes)}
        title="Перевести все языки?"
        message="Тексты уже заполненных переводов будут заменены переводом из полей выше. Картинки и файлы переводов останутся как есть."
        confirmButtonText="Перевести"
        confirmButtonVariant="primary"
      />
    </FormSection>
  );
};
